import { getIn, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { calculateBillRate, getRateByClass } from 'utils/fieldglass';

import { Grid, InputAdornment, TextField, Typography } from '@mui/material';

import { Job } from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import { formatCurrency3 } from '@libs/helpers/format';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import StepSection from '@libs/ui/components/step-section';

import { ApplicationFormState } from '../types';

interface RateInfoSectionProps {
  showOnlyRequired: boolean;
  job: Job;
}

const useAutoUpdateStandardBillRate = (job: Job) => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<ApplicationFormState>();

  const stRateNamePath = modelPath<ApplicationFormState>(
    (i) => i.rateInfo.standardRate,
  );
  const standardBillRatePath = modelPath<ApplicationFormState>(
    (i) => i.rateInfo.standardBillRate,
  );

  const currentSTRate = getIn(values, stRateNamePath);

  useEffect(() => {
    if (currentSTRate && typeof currentSTRate === 'number') {
      const calculatedBillRate = calculateBillRate(currentSTRate, job);
      setFieldValue(standardBillRatePath, calculatedBillRate);
    } else {
      setFieldValue(standardBillRatePath, null);
    }
    setFieldTouched(standardBillRatePath, true);
  }, [
    currentSTRate,
    job,
    setFieldValue,
    standardBillRatePath,
    setFieldTouched,
  ]);

  return getIn(values, standardBillRatePath);
};

const RateInfoSection: React.FC<RateInfoSectionProps> = ({
  showOnlyRequired,
  job,
}) => {
  const { values } = useFormikContext<ApplicationFormState>();
  const standardBillRate = useAutoUpdateStandardBillRate(job);

  const stRateNamePath = modelPath<ApplicationFormState>(
    (i) => i.rateInfo.standardRate,
  );
  const workerPayTypePath = modelPath<ApplicationFormState>(
    (i) => i.details.workerPayType,
  );
  const workerPayType = getIn(values, workerPayTypePath);
  const standardRate = getRateByClass(
    job,
    'ST',
    workerPayType ? { payType: workerPayType } : undefined,
  );
  return (
    <StepSection index={5} title="Pay rate Information">
      <Grid spacing={4} direction="column" container>
        <Grid item>
          <ConnectedTextField
            name={stRateNamePath}
            fullWidth
            variant="filled"
            type="number"
            label="ST/Hr - Standard Rate"
            InputProps={{
              endAdornment: <InputAdornment position="end">USD</InputAdornment>,
            }}
          />
          {standardRate.billAmountFormated &&
            standardRate.billAmountFormated !== 'N/A' && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Max Bill Rate: {standardRate.billAmountFormated}
              </Typography>
            )}
        </Grid>

        <Grid item>
          <TextField
            fullWidth
            value={
              standardBillRate
                ? formatCurrency3(standardBillRate, {
                    currency: 'USD',
                  })
                : ''
            }
            variant="filled"
            label="Standard Bill Rate"
            disabled
          />
        </Grid>
      </Grid>
    </StepSection>
  );
};

export default RateInfoSection;
